import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import cn from 'classnames'
import NextLink from 'next/link'
import type {HTMLAttributeAnchorTarget, ReactNode} from 'react'
import {memo} from 'react'
import type {TrackingProps} from 'src/common/helpers'
import {EVENTS, track} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import type {InteractionProps} from 'src/common/types'
import type {UrlObject} from 'url'

export type ButtonV2Variants = 'primary' | 'secondary' | 'tertiary' | 'icon-primary' | 'icon-secondary' | 'icon-dark'
export type ButtonV2Sizes = 'small' | 'medium'

type ButtonProps = {
  children: ReactNode
  variant: ButtonV2Variants
  className?: string
  id?: string
  disabled?: boolean
  trackingData: Omit<TrackingProps, 'event'>
  size?: ButtonV2Sizes
  border?: boolean
  newTrackingProps?: {interaction: Omit<InteractionProps, 'type'>; pageType?: string}
  hideOnMobile?: boolean
} & {
  href?: string | UrlObject
  target?: HTMLAttributeAnchorTarget
  onClick?: VoidFunction
  scroll?: boolean
  type?: 'submit' | 'button'
  locale?: string
  newTrackingProps?: {interaction: Omit<InteractionProps, 'type'>; pageType?: string}
  hideOnMobile?: boolean
}

export const ButtonV2 = memo(function ({
  variant,
  children,
  className,
  onClick,
  trackingData,
  href,
  target,
  locale,
  id,
  type,
  disabled,
  size = 'medium',
  border = true,
  newTrackingProps,
  hideOnMobile = false,
}: ButtonProps) {
  const isIconButton = variant === 'icon-primary' || variant === 'icon-secondary' || variant === 'icon-dark'
  const domainCountry = useGetDomainCountry()

  const generatedClassname = cn(
    'group pointer-events-auto relative isolate flex items-center justify-center text-nowrap rounded-[4px] uppercase',
    {
      'text-black': variant !== 'icon-dark',
      'bg-[rgba(255,_255,_255,_0.15)] text-white hover:bg-[rgba(255,_255,_255,_0.25)]': variant === 'icon-dark',
      'bg-green-neon hover:bg-primary-button-hover': variant === 'primary' || variant === 'icon-primary',
      'bg-moss-white lg:hover:bg-moss-grey-100': variant === 'secondary' || variant === 'icon-secondary',
      'bg-moss-grey-100 hover:bg-moss-grey-100/[90%]': variant === 'tertiary',
      'max-md:h-[38px] h-12 text-button-s': size === 'medium',
      'h-8 lg:h-[38px] text-button-s': size === 'small',
      'px-4 py-3': !isIconButton,
      'max-md:hidden': hideOnMobile,
    },
    className,
  )

  const handleClick = () => {
    track({
      event: EVENTS.CLICK,
      ...trackingData,
    })

    if (newTrackingProps) {
      trackMossAnalytics({
        event: 'interaction',
        interaction: {...newTrackingProps.interaction, type: 'click'},
        page: {
          domainCountry,
          type: newTrackingProps.pageType || 'unknown',
        },
      })
    }

    onClick?.()
  }

  if (href) {
    return (
      <NextLink
        className={generatedClassname}
        href={href}
        target={target}
        locale={locale}
        {...(id && {id})}
        onClick={handleClick}
      >
        <span className="z-10">{children}</span>
        {border && (
          <div
            className={cn('pointer-events-none absolute inset-0 rounded-[4px] border', {
              'border-black/[10%]': variant !== 'icon-dark',
              'border-[rgba(255,_255,_255,_0.10)]': variant === 'icon-dark',
              'group-hover:bg-moss-white/[20%] z-0': variant === 'primary' || variant === 'icon-primary',
            })}
          />
        )}
      </NextLink>
    )
  }

  return (
    <button
      className={generatedClassname}
      onClick={handleClick}
      type={type || 'button'}
      {...(id && {id})}
      disabled={disabled}
    >
      {children}
      {border && (
        <div
          className={cn('pointer-events-none absolute inset-0 rounded-[4px] border', {
            'border-black/[10%]': variant !== 'icon-dark',
            'border-[rgba(255,_255,_255,_0.10)]': variant === 'icon-dark',
            'z-0': variant === 'primary' || variant === 'icon-primary',
            'group-hover:bg-moss-white/[20%]': (variant === 'primary' || variant === 'icon-primary') && !disabled,
          })}
        />
      )}
    </button>
  )
})
